import { type ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { useMutation } from '@tanstack/react-query';
import { compareDesc, parseISO } from 'date-fns';
import { ImageIcon, PlusIcon } from 'lucide-react';

import { Button } from '@/components/new-ui/button';

import type { ProjectImageType, ProjectLayoutType } from '@/api/media';
import { env } from '@/constants/env';
import { cn } from '@/lib/utils';
import { MediaEmptyState } from '@/features/estate-media/components/media-empty-state';
import {
  getMediaItemName,
  MediaDateGroup,
  MediaSelectionBar,
  type MediaItem,
  useMediaItemActions,
} from '@/features/estate-media/components/media-item-card';
import {
  ViewMediaModal,
  type ViewMediaModalProps,
} from '@/features/estate-media/components/view-media-modal';

import { MediaDataLayout, type MediaDataLayoutProps } from './media-data-layout';
import { useModal } from '@/components/modals/new/use-modal';

type MediaDataProps = {
  type: 'project';
  data: Record<string, ProjectImageType[] | ProjectLayoutType[]>;
  handleDelete: (ids: string[], callback: () => void) => void;
  // shown between the section header and the media grid, outside of the new design scope
  topContent?: ReactNode;
  // required so every media page decides explicitly whether write actions are shown
  canWriteMedia: boolean;
} & MediaDataLayoutProps;

export const MediaData = ({ data, handleDelete, topContent, ...props }: MediaDataProps) => {
  const { t } = useTranslation();
  const contentData = useMemo<[string, ProjectImageType[]][]>(() => {
    return Object.entries(data).sort(([date1], [date2]) =>
      compareDesc(parseISO(date1), parseISO(date2))
    );
  }, [data]);
  const allImages = useMemo(() => contentData.flatMap(([, imgs]) => imgs), [contentData]);

  const mediaModal = useModal();
  const [mediaModalContext, setMediaModalContext] = useState<Omit<ViewMediaModalProps, 'modal'>>();

  const [isSelected, setSelected] = useState(false);
  const [selectedValues, setSelectedValues] = useState<
    { id: string; mediaId: string; name: string; date: Date }[]
  >([]);

  const handleSetSelectValue = () => {
    setSelected((prev) => !prev);
    setSelectedValues([]);
  };

  const handleSelect = ({
    id,
    mediaId,
    name,
    date,
  }: {
    id: string;
    mediaId: string;
    name: string;
    date: Date;
  }) => {
    //If the given image is already selected, it removes it from the selectedValues array.
    //If the given image is not already selected, it adds it to the selectedValues array.
    if (selectedValues.some((item) => item.mediaId === mediaId)) {
      setSelectedValues(selectedValues.filter((val) => val.mediaId !== mediaId));
    } else {
      setSelectedValues([...selectedValues, { id, mediaId, name, date }]);
    }
  };

  const toggleDateSelection = (images: ProjectImageType[]) => {
    setSelectedValues((prevSelected) => {
      const deletableImages = images.filter((image) => !image.isUsedByFlatfinder);
      if (!deletableImages.length) return prevSelected;

      // Check if all images for the given date are already selected
      const allSelected = deletableImages.every((img) =>
        prevSelected.some((selected) => selected.mediaId === img.mediaId)
      );

      if (allSelected) {
        // If all images are selected, remove them from the selected list (unselect all)
        return prevSelected.filter(
          (img) => !deletableImages.some((image) => image.mediaId === img.mediaId)
        );
      }

      // If not all images are selected, add the missing ones to the selection
      const newImages = deletableImages
        .map((image) => ({
          id: image.id,
          mediaId: image.mediaId || image.id,
          name: image.fileName || `image_${image.id}`,
          date: new Date(image.createdAt),
        }))
        .filter((img) => !prevSelected.some((selected) => selected.mediaId === img.mediaId)); // Avoid adding duplicates

      // Return updated selected values, merging previous selections with newly added ones
      return [...prevSelected, ...newImages];
    });
  };

  const exitSelectMode = () => {
    setSelected(false);
    setSelectedValues([]);
  };

  const {
    download: downloadItem,
    setThumbnail,
    thumbnailId,
  } = useMediaItemActions({
    type: 'project',
    id: props.projectId,
    page: props.page,
  });

  const handleDeleteSelectValues = () => {
    if (selectedValues.length === 0) {
      toast.error(t('no-files-selected'));
    } else {
      handleDelete(
        selectedValues.map((val) => val.id),
        exitSelectMode
      );
    }
  };

  const { mutate: downloadImages } = useMutation({
    mutationFn: async ({
      id,
      images,
      grouped = false,
    }: {
      id: string;
      images: { mediaId: string; date: Date; name: string }[];
      grouped?: boolean;
    }) => {
      // Use fetch directly for binary data to bypass any potential
      // processing that Eden or TanStack Query might do
      const response = await fetch(`${env.API_URL}/projects/${id}/images/zip?grouped=${grouped}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ images }),
      });

      if (!response.ok) {
        throw new Error(`Download failed with status: ${response.status}`);
      }

      return response.arrayBuffer();
    },
    onSuccess: (data) => {
      // Handle download as before
      const blob = new Blob([data], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'images.zip';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
  });

  const handleDownload = async () => {
    if (selectedValues.length === 0) {
      toast.error(t('no-files-selected'));
    } else {
      // Extract unique dates
      const uniqueDates = new Set(
        selectedValues.map((image) => image.date.toISOString().split('T')[0])
      );
      // Check if there are multiple unique dates
      const hasMultipleDates = uniqueDates.size > 1;

      downloadImages({ id: props.projectId, images: selectedValues, grouped: hasMultipleDates });
    }
  };

  const openViewMediaModal = (args: Omit<ViewMediaModalProps, 'modal'>) => {
    setMediaModalContext(args);
    mediaModal.open();
  };

  const handleItemClick = (file: ProjectImageType) => {
    if (isSelected) {
      if (file.isUsedByFlatfinder) return;
      handleSelect({
        id: file.id,
        mediaId: file.mediaId || file.id,
        name: getMediaItemName(file) || `image_${file.id}`,
        date: new Date(file.createdAt),
      });
      return;
    }
    const group = contentData.find(([, items]) => items.includes(file))?.[1] ?? [file];
    openViewMediaModal({ images: group, preselectedCurrentIndex: group.indexOf(file) });
  };

  return (
    <>
      <MediaDataLayout
        isSelecting={isSelected}
        selectedCount={selectedValues.length}
        onSelect={handleSetSelectValue}
        onCancelSelect={exitSelectMode}
        onDelete={handleDeleteSelectValues}
        onDownload={handleDownload}
        {...props}
      >
        {topContent}
        {isSelected && contentData.length > 0 ? (
          <MediaSelectionBar
            selectedCount={selectedValues.length}
            isAllSelected={
              allImages.some((img) => !img.isUsedByFlatfinder) &&
              allImages
                .filter((img) => !img.isUsedByFlatfinder)
                .every((img) => selectedValues.some((item) => item.mediaId === img.mediaId))
            }
            onToggleAll={() => toggleDateSelection(allImages)}
          />
        ) : null}
        {contentData.length > 0 ? (
          // A little more room below the selection row in select mode
          <div className={cn('flex flex-col gap-5', isSelected && 'mt-2')}>
            {contentData.map(([date, mediaData]) => {
              return (
                <MediaDateGroup
                  thumbnailId={thumbnailId}
                  key={date}
                  date={date}
                  items={mediaData as MediaItem[]}
                  isSelecting={isSelected}
                  isItemSelected={(file) =>
                    selectedValues.some((item) => item.mediaId === file.mediaId)
                  }
                  onItemClick={(file) => handleItemClick(file as ProjectImageType)}
                  onItemDownload={downloadItem}
                  onItemSetThumbnail={
                    props.canWriteMedia && props.page === 'images' ? setThumbnail : undefined
                  }
                />
              );
            })}
          </div>
        ) : (
          <MediaEmptyState
            icon={<ImageIcon />}
            action={
              props.canWriteMedia && props.onUpload ? (
                <Button size="sm" className="gap-1.5" onClick={props.onUpload}>
                  <PlusIcon className="h-4 w-4" />
                  {t('uploadMedia')}
                </Button>
              ) : null
            }
          />
        )}
      </MediaDataLayout>
      {mediaModalContext ? (
        <ViewMediaModal
          {...mediaModalContext}
          imageBeautificationButtons={props.canWriteMedia}
          modal={mediaModal}
          projectId={props.projectId}
          layout="split"
        />
      ) : null}
    </>
  );
};
